import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useId,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
  type FormEvent,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";
import { useTranslation } from "react-i18next";
import {
  Send,
  Paperclip,
  AtSign,
  Smile,
  Bold,
  Italic,
  Link2,
  Code,
  List,
  ListOrdered,
  Quote,
  X,
} from "lucide-react";
import { Textarea } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { Avatar } from "@/components/ui/Avatar";
import { getTone } from "@/lib/i18n";
import { cn } from "@/lib/cn";
import type { User } from "@/lib/types";
import { formatBytes } from "@/lib/format";

export interface PendingAttachment {
  id: string;
  file: File;
  /** ObjectURL for image previews. */
  preview?: string;
}

export interface MessageComposerProps {
  onSend: (
    body: string,
    extras?: { attachments?: File[]; mentions?: string[] },
  ) => void;
  onTyping?: () => void;
  members?: User[];
  disabled?: boolean;
  draftKey?: string;
  placeholder?: string;
  /** Pre-fill the textarea (e.g. when quoting). */
  initialValue?: string;
  /**
   * Text to add to the end of the current draft (e.g. a quoted message).
   * `nonce` changes on every request so quoting the same message twice still inserts.
   */
  insertRequest?: { text: string; nonce: number } | null;
  /** Reply target — shown as a small banner above the input. */
  replyingTo?: { id: string; author: string; preview: string } | null;
  onCancelReply?: () => void;
}

const QUICK_EMOJI = ["👍", "🙏", "✅", "🎉", "🔥", "👀", "❤️", "😄", "😅", "🤝"];

export function MessageComposer({
  onSend,
  members = [],
  onTyping,
  disabled,
  draftKey,
  placeholder,
  initialValue = "",
  insertRequest,
  replyingTo,
  onCancelReply,
}: MessageComposerProps): JSX.Element {
  const { t } = useTranslation();
  const [value, setValue] = useState(() => initialValue || (draftKey ? sessionStorage.getItem(draftKey) || "" : ""));
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [mention, setMention] = useState<{
    open: boolean;
    query: string;
    /** Caret position immediately after the `@`. */
    start: number;
  }>({ open: false, query: "", start: 0 });
  const [mentionIndex, setMentionIndex] = useState(0);
  const mentionListId = useId();
  const [hovering, setHovering] = useState(false);
  const [attachments, setAttachments] = useState<PendingAttachment[]>([]);

  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const pendingCaret = useRef<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const tone = getTone();
  useLayoutEffect(() => {
    if (pendingCaret.current === null) return;
    textareaRef.current?.focus();
    textareaRef.current?.setSelectionRange(pendingCaret.current, pendingCaret.current);
    pendingCaret.current = null;
  }, [value]);

  useEffect(() => { if (initialValue) setValue(initialValue); }, [initialValue]);
  useEffect(() => {
    if (!insertRequest?.text) return;
    setValue(current => {
      const next = current.trim() ? `${current.replace(/\s+$/, "")}\n\n${insertRequest.text}` : insertRequest.text;
      pendingCaret.current = next.length;
      return next;
    });
  }, [insertRequest]);
  useEffect(() => { if (draftKey) { if (value) sessionStorage.setItem(draftKey, value); else sessionStorage.removeItem(draftKey); } }, [draftKey, value]);
  const attachmentsRef = useRef(attachments);
  attachmentsRef.current = attachments;

  // Auto-grow textarea up to a cap.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 240)}px`;
  }, [value]);

  // Clean up object URLs on unmount.
  useEffect(() => {
    return () => {
      attachmentsRef.current.forEach((a) => {
        if (a.preview) URL.revokeObjectURL(a.preview);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = useCallback(
    (e?: FormEvent) => {
      e?.preventDefault();
      const trimmed = value.trim();
      if ((!trimmed && attachments.length === 0) || disabled) return;
      const hasMention = (name: string) => {
        const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        return new RegExp(`(^|\\s)@${escaped}(?=\\s|$|[.,!?、。])`, "u").test(trimmed);
      };
      const mentions = Array.from(new Set([
        ...members.filter(member => hasMention(member.displayName) || hasMention(member.id)).map(member => member.id),
        ...["here", "channel"].filter(hasMention),
      ]));
      // Keep the legacy single-arg call signature when there are no extras —
      // existing tests assert `onSend("body")` with one argument exactly.
      if (attachments.length === 0 && mentions.length === 0) {
        onSend(trimmed);
      } else {
        onSend(trimmed, {
          attachments: attachments.map((a) => a.file),
          mentions,
        });
      }
      // Reset
      attachments.forEach((a) => {
        if (a.preview) URL.revokeObjectURL(a.preview);
      });
      setAttachments([]);
      setValue("");
      // Refocus the textarea so the next message can be typed immediately.
      textareaRef.current?.focus();
    },
    [value, attachments, disabled, onSend, members],
  );

  const onKey = (e: ReactKeyboardEvent<HTMLTextAreaElement>) => {
    if (e.nativeEvent.isComposing) return;
    if (mention.open && filteredMentions.length > 0) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault(); setMentionIndex(index => (index + (e.key === "ArrowDown" ? 1 : -1) + filteredMentions.length) % filteredMentions.length); return;
      }
      if (e.key === "Enter" && !e.ctrlKey && !e.metaKey) { e.preventDefault(); pickMention(filteredMentions[mentionIndex % filteredMentions.length].displayName); return; }
      // Mention popup steals arrow / escape / enter.
      if (e.key === "Escape") {
        e.preventDefault();
        setMention((m) => ({ ...m, open: false }));
        return;
      }
    }
    if ((e.metaKey || e.ctrlKey) && !e.altKey && ["b", "i"].includes(e.key.toLowerCase())) { e.preventDefault(); wrapSelection(e.key.toLowerCase() === "b" ? "**" : "*"); return; }
    if ((e.metaKey || e.ctrlKey) && !e.altKey && e.key === "Enter") {
      e.preventDefault();
      submit();
      return;
    }
    onTyping?.();
  };

  const onChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const v = e.target.value;
    setValue(v);
    // Mention popup: detect @-trigger to the left of the caret.
    const caret = e.target.selectionStart ?? v.length;
    const upTo = v.slice(0, caret);
    const m = /(?:^|\s)@([^\s@]*)$/.exec(upTo);
    if (m) {
      setMentionIndex(0);
      setMention({ open: true, query: m[1], start: caret - m[1].length });
    } else if (mention.open) {
      setMention((p) => ({ ...p, open: false }));
    }
    onTyping?.();
  };

  const insertAtCaret = (text: string, opts?: { wrap?: boolean }) => {
    const el = textareaRef.current;
    if (!el) return;
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? value.length;
    const before = value.slice(0, start);
    const sel = value.slice(start, end);
    const after = value.slice(end);
    const next = opts?.wrap
      ? `${before}${text}${sel}${text}${after}`
      : `${before}${text}${after}`;
    pendingCaret.current = opts?.wrap ? start + text.length + sel.length : start + text.length;
    setValue(next);
  };

  const wrapSelection = (left: string, right: string = left) => {
    const el = textareaRef.current;
    if (!el) return;
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? value.length;
    const before = value.slice(0, start);
    const sel = value.slice(start, end);
    const after = value.slice(end);
    const next = `${before}${left}${sel || ""}${right}${after}`;
    pendingCaret.current = sel ? start + left.length + sel.length + right.length : start + left.length;
    setValue(next);
  };

  const linePrefix = (prefix: string) => {
    const el = textareaRef.current;
    if (!el) return;
    const start = el.selectionStart ?? value.length;
    const lineStart = value.lastIndexOf("\n", start - 1) + 1;
    const next = value.slice(0, lineStart) + prefix + value.slice(lineStart);
    pendingCaret.current = start + prefix.length;
    setValue(next);
  };

  const filteredMentions = useMemo(() => {
    if (!mention.open) return [];
    const q = mention.query.toLowerCase();
    return members.filter(
      (u) =>
        u.displayName.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q),
    ).slice(0, 6);
  }, [mention, members]);

  const pickMention = (name: string) => {
    const el = textareaRef.current;
    if (!el) return;
    const before = value.slice(0, mention.start - 1); // strip the `@`
    const after = value.slice((el.selectionStart ?? value.length));
    const insertion = `@${name} `;
    const next = `${before}${insertion}${after}`;
    pendingCaret.current = before.length + insertion.length;
    setValue(next);
    setMention((p) => ({ ...p, open: false }));
  };

  const addFiles = useCallback((files: FileList | File[]) => {
    const incoming = Array.from(files).map<PendingAttachment>((file) => {
      const isImg = file.type.startsWith("image/");
      return {
        id: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2, 6)}`,
        file,
        preview: isImg ? URL.createObjectURL(file) : undefined,
      };
    });
    setAttachments((prev) => [...prev, ...incoming]);
  }, []);

  const onDrop = (e: DragEvent<HTMLFormElement>) => {
    e.preventDefault();
    setHovering(false);
    if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
  };

  const onDragOver = (e: DragEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (e.dataTransfer.types.includes("Files")) setHovering(true);
  };

  const onDragLeave = (e: DragEvent<HTMLFormElement>) => {
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setHovering(false);
  };

  const removeAttachment = (id: string) => {
    setAttachments((prev) => {
      const next = prev.filter((a) => a.id !== id);
      const removed = prev.find((a) => a.id === id);
      if (removed?.preview) URL.revokeObjectURL(removed.preview);
      return next;
    });
  };

  const ph =
    placeholder ??
    (t(`compose.placeholder.${tone}`, {
      defaultValue: t("compose.placeholder.polite"),
    }) as string);

  const canSend = (value.trim().length > 0 || attachments.length > 0) && !disabled;

  return (
    <form
      onSubmit={submit}
      onDrop={onDrop}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      className={cn(
        "border-t border-border-subtle bg-canvas px-4 pt-2 pb-3",
        "flex flex-col gap-2 relative",
        hovering && "ring-2 ring-brand-600 ring-inset",
      )}
    >
      {hovering ? (
        <div
          aria-hidden
          className="absolute inset-0 z-10 bg-brand-50/90 border-2 border-dashed border-brand-600 rounded-md flex items-center justify-center pointer-events-none"
        >
          <p className="text-brand-700 font-medium">
            {t("files.drop") as string}
          </p>
        </div>
      ) : null}

      {replyingTo ? (
        <div className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-surface-base border border-border-subtle text-xs">
          <span className="text-fg-muted">
            {t("message.replyingTo", {
              name: replyingTo.author,
              defaultValue: "{{name}} さんへ返信",
            }) as string}
          </span>
          <span className="flex-1 truncate text-fg-secondary italic">
            "{replyingTo.preview}"
          </span>
          <button
            type="button"
            onClick={() => onCancelReply?.()}
            aria-label={t("common.cancel") as string}
            className="p-0.5 rounded hover:bg-surface-raised text-fg-muted"
          >
            <X size={12} aria-hidden />
          </button>
        </div>
      ) : null}

      {/* Markdown toolbar */}
      <div
        role="toolbar"
        aria-label={t("compose.toolbar", { defaultValue: "書式設定" }) as string}
        className="flex items-center gap-0.5 text-fg-muted"
      >
        <ToolbarButton
          onClick={() => wrapSelection("**")}
          label={t("compose.bold", { defaultValue: "太字" }) as string}
          shortcut="Ctrl+B"
        >
          <Bold size={14} aria-hidden />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => wrapSelection("*")}
          label={t("compose.italic", { defaultValue: "斜体" }) as string}
          shortcut="Ctrl+I"
        >
          <Italic size={14} aria-hidden />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => {
            const url = window.prompt(
              t("compose.link", { defaultValue: "URL を入力してください" }) as string,
              "https://",
            );
            if (url) wrapSelection("[", `](${url})`);
          }}
          label={t("compose.link", { defaultValue: "リンク" }) as string}
        >
          <Link2 size={14} aria-hidden />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => insertAtCaret("`", { wrap: true })}
          label={t("compose.code", { defaultValue: "コード" }) as string}
        >
          <Code size={14} aria-hidden />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => linePrefix("- ")}
          label={t("compose.bulletList", { defaultValue: "箇条書き" }) as string}
        >
          <List size={14} aria-hidden />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => linePrefix("1. ")}
          label={t("compose.numberedList", { defaultValue: "番号付き" }) as string}
        >
          <ListOrdered size={14} aria-hidden />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => linePrefix("> ")}
          label={t("compose.quoteBlock", { defaultValue: "引用" }) as string}
        >
          <Quote size={14} aria-hidden />
        </ToolbarButton>
      </div>

      <Textarea
        ref={textareaRef}
        value={value}
        onChange={onChange}
        onKeyDown={onKey}
        placeholder={ph}
        aria-autocomplete="list"
        aria-controls={mention.open ? mentionListId : undefined}
        aria-activedescendant={mention.open && filteredMentions.length ? `${mentionListId}-${mentionIndex}` : undefined}
        rows={2}
        disabled={disabled}
        aria-label={ph}
        className="min-h-16 max-h-60 leading-6"
      />

      {attachments.length > 0 ? (
        <ul className="flex flex-wrap gap-2">
          {attachments.map((a) => (
            <li
              key={a.id}
              className="flex items-center gap-2 px-2 py-1 rounded-md border border-border-subtle bg-surface-base text-xs"
            >
              {a.preview ? (
                <img
                  src={a.preview}
                  alt={a.file.name}
                  className="w-7 h-7 rounded object-cover"
                />
              ) : (
                <span className="w-7 h-7 rounded bg-brand-50 text-brand-700 flex items-center justify-center">
                  <Paperclip size={12} aria-hidden />
                </span>
              )}
              <span className="max-w-40 truncate" title={a.file.name}>
                {a.file.name}
              </span>
              <span className="text-fg-muted">{formatBytes(a.file.size)}</span>
              <button
                type="button"
                onClick={() => removeAttachment(a.id)}
                aria-label={t("common.delete") as string}
                className="p-0.5 rounded hover:bg-surface-raised text-fg-muted"
              >
                <X size={12} aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {mention.open && filteredMentions.length > 0 ? (
        <div
          role="listbox"
          id={mentionListId}
          aria-label="Mention"
          className="absolute bottom-full left-4 mb-1 bg-canvas border border-border-subtle rounded-md shadow-lg py-1 min-w-56 z-20"
        >
          {filteredMentions.map((u, index) => (
            <button
              key={u.id}
              type="button"
              role="option"
              id={`${mentionListId}-${index}`}
              aria-selected={mentionIndex === index}
              onMouseEnter={() => setMentionIndex(index)}
              onMouseDown={event => event.preventDefault()}
              onClick={() => pickMention(u.displayName)}
              className={cn("w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-surface-raised focus-visible:bg-surface-raised text-sm", mentionIndex === index && "bg-brand-50")}
            >
              <Avatar name={u.displayName} size={20} />
              <span className="font-medium">{u.displayName}</span>
              <span className="text-xs text-fg-muted truncate">{u.email}</span>
            </button>
          ))}
        </div>
      ) : null}

      <div className="flex items-center gap-1">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          hidden
          onChange={(e) => {
            if (e.target.files) addFiles(e.target.files);
            e.target.value = "";
          }}
        />
        <ToolbarButton
          onClick={() => fileInputRef.current?.click()}
          label={t("files.upload") as string}
        >
          <Paperclip size={16} aria-hidden />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => {
            insertAtCaret("@");
            textareaRef.current?.focus();
          }}
          label={t("compose.mention", { defaultValue: "メンション" }) as string}
        >
          <AtSign size={16} aria-hidden />
        </ToolbarButton>
        <div className="relative">
          <ToolbarButton
            onClick={() => setEmojiOpen((v) => !v)}
            label={t("message.react") as string}
            aria-haspopup="menu"
            aria-expanded={emojiOpen}
          >
            <Smile size={16} aria-hidden />
          </ToolbarButton>
          {emojiOpen ? (
            <div
              role="menu"
              className="absolute bottom-full mb-1 left-0 bg-canvas border border-border-subtle rounded-md shadow-lg p-1.5 z-20"
            >
              <div className="grid grid-cols-5 gap-1">
                {QUICK_EMOJI.map((emo) => (
                  <button
                    key={emo}
                    type="button"
                    onClick={() => {
                      insertAtCaret(emo);
                      setEmojiOpen(false);
                    }}
                    className="w-7 h-7 flex items-center justify-center rounded hover:bg-surface-raised focus-visible:shadow-focus text-md"
                    aria-label={emo}
                  >
                    {emo}
                  </button>
                ))}
              </div>
            </div>
          ) : null}
        </div>
        <span className="text-xs text-fg-muted ml-2">
          {t("compose.sendShortcut")}
        </span>
        <span className="flex-1" />
        <Button
          type="submit"
          size="sm"
          disabled={!canSend}
          leadingIcon={<Send size={14} aria-hidden />}
        >
          {t(`compose.send.${tone}`, { defaultValue: t("compose.send.polite") }) as string}
        </Button>
      </div>
    </form>
  );
}

function ToolbarButton({
  children,
  label,
  shortcut,
  onClick,
  ...rest
}: {
  children: React.ReactNode;
  label: string;
  shortcut?: string;
  onClick?: () => void;
} & Pick<React.ButtonHTMLAttributes<HTMLButtonElement>, "aria-haspopup" | "aria-expanded">): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={shortcut ? `${label} (${shortcut})` : label}
      className="p-1.5 rounded text-fg-muted hover:bg-surface-raised hover:text-fg-primary focus-visible:shadow-focus"
      {...rest}
    >
      {children}
    </button>
  );
}
